// Runtime helpers for the tier / trin curriculum tree.

import {
  ALL_TRINS,
  getTrin,
  getTier,
  MINI_CHECKPOINTS,
} from '../data/curriculum';
import { PROGRESSION } from './featureFlags';
import type {
  CurriculumTier,
  CurriculumTierId,
  MiniCheckpoint,
  TierCheckpoint,
  Trin,
  UserProfile,
} from '../types';

const STRICT_CASE_TIER_ORDER: CurriculumTierId[] = [
  'grundniveau',
  'niveau-1',
  'niveau-2',
  'niveau-3',
  'niveau-4',
  'niveau-5',
  'niveau-6',
];

/** Case-sensitive typing from niveau 5, bonus extras, or explicit trin flag. */
export function trinUsesStrictCase(trin: Trin): boolean {
  if (trin.strictCase === true) return true;
  if (trin.strictCase === false) return false;
  if (trin.id.startsWith('__bonus_')) return true;
  const fromIdx = STRICT_CASE_TIER_ORDER.indexOf(PROGRESSION.strictCaseFromTier);
  const tierIdx = STRICT_CASE_TIER_ORDER.indexOf(trin.tierId);
  return fromIdx >= 0 && tierIdx >= fromIdx;
}

export {
  ALL_TRINS,
  CHECKPOINTS,
  CURRICULUM,
  getCheckpoint,
  getCheckpointAfterTier,
  getTrin,
  getTier,
} from '../data/curriculum';

export const FIRST_TRIN_ID = 'grundniveau-trin-1';

export function isTester(user: UserProfile): boolean {
  return import.meta.env.DEV && user.role === 'tester';
}

/** Danish label for a tier id. */
export function tierTitle(tierId: CurriculumTierId): string {
  return getTier(tierId)?.title ?? tierId;
}

/** e.g. "Grundniveau · Trin 3" */
export function trinLabel(trin: Trin): string {
  return `${tierTitle(trin.tierId)} · Trin ${trin.trinNumber}`;
}

/** Label from a stored trin id (lesson history, etc.). */
export function trinLabelFromId(trinId: string): string {
  const trin = getTrin(trinId);
  return trin ? trinLabel(trin) : trinId;
}

export function checkpointLabel(checkpoint: TierCheckpoint): string {
  return `${tierTitle(checkpoint.afterTierId)} · ${checkpoint.title}`;
}

export function isTierPlanned(tier: CurriculumTier): boolean {
  return tier.trins.length === 0;
}

/** Status badge text for a tier in lists. */
export function tierStatusLabel(
  tier: CurriculumTier,
  opts: { unlocked: boolean; completed: boolean },
): string {
  if (isTierPlanned(tier)) return 'Planlagt';
  if (opts.completed) return 'Gennemført';
  if (opts.unlocked) return 'Åbent';
  return 'Låst';
}

export function getActiveTrin(user: UserProfile): Trin {
  return getTrin(user.currentTrinId) ?? getTrin(FIRST_TRIN_ID)!;
}

export function firstTrinIdInTier(tierId: CurriculumTierId): string | null {
  const tier = getTier(tierId);
  if (!tier || tier.trins.length === 0) return null;
  return tier.trins[0].id;
}

/** Index of trin in the global ALL_TRINS list. */
export function trinIndex(trinId: string): number {
  return ALL_TRINS.findIndex((t) => t.id === trinId);
}

export function getNextTrin(trinId: string): Trin | null {
  const i = trinIndex(trinId);
  if (i < 0 || i >= ALL_TRINS.length - 1) return null;
  return ALL_TRINS[i + 1];
}

export function isTrinCompleted(user: UserProfile, trinId: string): boolean {
  return user.completedTrinIds.includes(trinId);
}

/** All standard trins in a tier are in completedTrinIds. */
export function isTierCompleted(user: UserProfile, tierId: CurriculumTierId): boolean {
  if (user.completedTierIds.includes(tierId)) return true;
  const tier = getTier(tierId);
  if (!tier || tier.trins.length === 0) return false;
  return tier.trins.every((t) => user.completedTrinIds.includes(t.id));
}

/** User may play trins in unlocked tiers. Checkpoints do not affect tier lock. */
export function isTierUnlocked(
  user: UserProfile,
  tierId: CurriculumTierId,
): boolean {
  if (isTester(user)) {
    const tier = getTier(tierId);
    return tier !== undefined && !isTierPlanned(tier);
  }
  if (tierId === 'grundniveau') return true;
  const order: CurriculumTierId[] = [
    'grundniveau',
    'niveau-1',
    'niveau-2',
    'niveau-3',
    'niveau-4',
    'niveau-5',
    'niveau-6',
  ];
  const idx = order.indexOf(tierId);
  if (idx <= 0) return true;
  const prev = order[idx - 1];
  return isTierCompleted(user, prev);
}

/** Whether this trin row should appear selectable on the dashboard. */
export function isTrinAccessible(user: UserProfile, trin: Trin): boolean {
  if (isTester(user)) {
    const tier = getTier(trin.tierId);
    return tier !== undefined && !isTierPlanned(tier);
  }
  if (!isTierUnlocked(user, trin.tierId)) return false;
  if (isTrinCompleted(user, trin.id)) return true;
  const activeIdx = trinIndex(user.currentTrinId);
  const thisIdx = trinIndex(trin.id);
  return thisIdx <= activeIdx;
}

export function allTrinIdsInTier(tierId: CurriculumTierId): string[] {
  return getTier(tierId)?.trins.map((t) => t.id) ?? [];
}

/** All standard trins in afterTierId are completed — checkpoint becomes visible. */
export function isCheckpointVisible(
  user: UserProfile,
  checkpoint: TierCheckpoint,
): boolean {
  if (isTester(user)) return true;
  const ids = allTrinIdsInTier(checkpoint.afterTierId);
  if (ids.length === 0) return false;
  return ids.every((id) => user.completedTrinIds.includes(id));
}

export function isCheckpointAccessible(
  user: UserProfile,
  checkpoint: TierCheckpoint,
): boolean {
  return isCheckpointVisible(user, checkpoint);
}

export function canStartTrin(trin: Trin): boolean {
  return trin.rounds.length > 0;
}

export function canStartCheckpoint(checkpoint: TierCheckpoint): boolean {
  return checkpoint.sprintWords.length > 0;
}

export {
  getMiniCheckpoint,
  getMiniCheckpointAfterTrin,
  miniCheckpointsInTier,
} from '../data/curriculum';

export function hasPassedTrinProof(user: UserProfile, trinId: string): boolean {
  if (!PROGRESSION.trinProof || !PROGRESSION.requireTrinProof) return true;
  return !!user.trinProofRecords?.[trinId]?.lastPassedAt;
}

export function isMiniCheckpointPassed(
  user: UserProfile,
  checkpointId: string,
): boolean {
  return (user.passedMiniCheckpointIds ?? []).includes(checkpointId);
}

export function isMiniCheckpointAccessible(
  user: UserProfile,
  checkpoint: MiniCheckpoint,
): boolean {
  if (isTester(user)) return true;
  return user.completedTrinIds.includes(checkpoint.afterTrinId);
}

export function isMiniCheckpointRecommended(
  user: UserProfile,
  checkpoint: MiniCheckpoint,
): boolean {
  if (!PROGRESSION.miniCheckpoints) return false;
  return (
    isMiniCheckpointAccessible(user, checkpoint) &&
    !isMiniCheckpointPassed(user, checkpoint.id)
  );
}

export function canStartMiniCheckpoint(checkpoint: MiniCheckpoint): boolean {
  return checkpoint.sprintWords.length > 0;
}

/** First recommended mini-checkpoint for dashboard banner. */
export function getRecommendedMiniCheckpoint(
  user: UserProfile,
): MiniCheckpoint | undefined {
  if (!PROGRESSION.miniCheckpoints) return undefined;
  return MINI_CHECKPOINTS.find((c) => isMiniCheckpointRecommended(user, c));
}
